// automationConditionFilters.js

import { isEnumCompatibleFieldType } from 'utilities/FieldTypes';

const DATE_RELATIVE_OPERATORS = new Set([
  'within_past',
  'within_next',
  'older_than',
  'is_overdue',
  'is_today',
  'is_this',
]);

const DATERANGE_RELATIVE_OPERATORS = new Set([
  'overlaps_past',
  'overlaps_next',
  'is_active',
  'overlaps_this',
]);

export const VALUELESS_CONDITION_OPERATORS = new Set([
  'is_empty',
  'is_not_empty',
  'is_complete',
  'is_not_complete',
  'is_overdue',
  'is_today',
  'is_active',
]);

export const CHECKLIST_ITEM_CONDITION_OPERATORS = new Set([
  'item_is_done',
  'item_is_not_done',
]);

export const RELATIVE_DATE_UNITS = [
  { value: 'days', label: 'Days' },
  { value: 'weeks', label: 'Weeks' },
  { value: 'months', label: 'Months' },
  { value: 'years', label: 'Years' },
];

export const RELATIVE_CALENDAR_UNITS = [
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'year', label: 'Year' },
];

const enumOperators = [
  { value: 'equals', label: 'is' },
  { value: 'is_not', label: 'is not' },
  { value: 'is_any_of', label: 'is any of' },
  { value: 'is_none_of', label: 'is none of' },
];

const tagOperators = [
  { value: 'equals', label: 'is' },
  { value: 'contains_any', label: 'contains any' },
  { value: 'contains_all', label: 'contains all' },
  { value: 'not_contains', label: 'does not contain' },
];

const peopleOperators = [
  { value: 'equals', label: 'is' },
  { value: 'contains_any', label: 'contains any' },
  { value: 'contains_all', label: 'contains all' },
  { value: 'not_contains', label: 'does not contain' },
];

const stringOperators = [
  { value: 'equals', label: 'is' },
  { value: 'not_equals', label: 'is not' },
  { value: 'contains', label: 'contains' },
  { value: 'starts_with', label: 'starts with' },
  { value: 'ends_with', label: 'ends with' },
  { value: 'is_empty', label: 'is empty' },
  { value: 'is_not_empty', label: 'is not empty' },
];

const numberOperators = [
  { value: 'equals', label: 'is' },
  { value: 'not_equals', label: 'is not' },
  { value: 'gt', label: 'greater than' },
  { value: 'gte', label: 'is at least' },
  { value: 'lt', label: 'less than' },
  { value: 'lte', label: 'is at most' },
  { value: 'between', label: 'is between' },
  { value: 'is_empty', label: 'is empty' },
  { value: 'is_not_empty', label: 'is not empty' },
];

const dateOperators = [
  { value: 'within_past', label: 'within past' },
  { value: 'within_next', label: 'within next' },
  { value: 'older_than', label: 'older than' },
  { value: 'is_overdue', label: 'is overdue' },
  { value: 'is_today', label: 'is today' },
  { value: 'is_this', label: 'is this' },
  { value: 'is_empty', label: 'is empty' },
  { value: 'is_not_empty', label: 'is not empty' },
];

const dateRangeOperators = [
  { value: 'overlaps_past', label: 'overlaps past' },
  { value: 'overlaps_next', label: 'overlaps next' },
  { value: 'is_active', label: 'is active now' },
  { value: 'overlaps_this', label: 'overlaps this' },
  { value: 'is_empty', label: 'is empty' },
  { value: 'is_not_empty', label: 'is not empty' },
];

const defaultOperators = [
  { value: 'equals', label: 'is' },
];

const measurementOperators = [
  { value: 'is_empty', label: 'is empty' },
  { value: 'is_not_empty', label: 'is not empty' },
];

const checklistOperators = [
  { value: 'is_complete', label: 'is complete' },
  { value: 'is_not_complete', label: 'is not complete' },
  { value: 'item_is_done', label: 'item is done' },
  { value: 'item_is_not_done', label: 'item is not done' },
  { value: 'is_empty', label: 'is empty' },
  { value: 'is_not_empty', label: 'is not empty' },
];

const getOperatorSpecs = (fieldType) => {
  if (fieldType === 'number' || fieldType === 'money' || fieldType === 'percentage') return numberOperators;
  if (fieldType === 'measurement') return measurementOperators;
  if (fieldType === 'string' || fieldType === 'text' || fieldType === 'longtext' || fieldType === 'textarea' || fieldType === 'richtext' || fieldType === 'url' || fieldType === 'email' || fieldType === 'phone') return stringOperators;
  if (isEnumCompatibleFieldType(fieldType)) return enumOperators;
  if (fieldType === 'checklist') return checklistOperators;
  if (fieldType === 'tags') return tagOperators;
  if (fieldType === 'people') return peopleOperators;
  if (fieldType === 'date' || fieldType === 'datetime') return dateOperators;
  if (fieldType === 'daterange' || fieldType === 'datetimerange') return dateRangeOperators;
  return defaultOperators;
};

export const normalizeRelativeUnit = (unit) => {
  const normalized = String(unit || '').toLowerCase();
  if (normalized === 'day' || normalized === 'days') return 'days';
  if (normalized === 'week' || normalized === 'weeks') return 'weeks';
  if (normalized === 'month' || normalized === 'months') return 'months';
  if (normalized === 'year' || normalized === 'years') return 'years';
  return '';
};

export const normalizeCalendarUnit = (unit) => {
  const normalized = String(unit || '').toLowerCase();
  if (normalized === 'week' || normalized === 'weeks') return 'week';
  if (normalized === 'month' || normalized === 'months') return 'month';
  if (normalized === 'year' || normalized === 'years') return 'year';
  return '';
};

export const isDateLikeFieldType = (fieldType) => (
  fieldType === 'date' || fieldType === 'datetime' || fieldType === 'daterange' || fieldType === 'datetimerange'
);

export const getAllowedConditionOperators = (fieldType) => (
  getOperatorSpecs(String(fieldType || '').trim().toLowerCase()).map((operator) => operator.value)
);

export const getConditionOperatorLabel = (operator) => {
  const normalizedOperator = String(operator || '').trim().toLowerCase();
  const allSpecs = [
    ...defaultOperators,
    ...stringOperators,
    ...numberOperators,
    ...measurementOperators,
    ...checklistOperators,
    ...enumOperators,
    ...tagOperators,
    ...peopleOperators,
    ...dateOperators,
    ...dateRangeOperators,
  ];

  const match = allSpecs.find((entry) => entry.value === normalizedOperator);
  if (match) return match.label;

  if (normalizedOperator === 'is') return 'is';
  if (normalizedOperator === 'is_not') return 'is not';
  return normalizedOperator;
};

export const getDefaultConditionOperator = (fieldType) => {
  const allowed = getAllowedConditionOperators(fieldType);
  return allowed[0] || 'equals';
};

export const operatorNeedsValue = (fieldType, operator) => {
  if (fieldType === 'date' || fieldType === 'datetime') {
    return operator === 'within_past' || operator === 'within_next' || operator === 'older_than';
  }

  if (fieldType === 'daterange' || fieldType === 'datetimerange') {
    return operator === 'overlaps_past' || operator === 'overlaps_next';
  }

  return false;
};

export const operatorNeedsUnit = (fieldType, operator) => {
  if (fieldType === 'date' || fieldType === 'datetime') {
    return operator === 'within_past' || operator === 'within_next' || operator === 'older_than' || operator === 'is_this';
  }

  if (fieldType === 'daterange' || fieldType === 'datetimerange') {
    return operator === 'overlaps_past'
      || operator === 'overlaps_next'
      || operator === 'overlaps_this';
  }

  return false;
};

export const isRelativeTemporalOperator = (fieldType, operator) => {
  if (fieldType === 'date' || fieldType === 'datetime') {
    return DATE_RELATIVE_OPERATORS.has(operator);
  }

  if (fieldType === 'daterange' || fieldType === 'datetimerange') {
    return DATERANGE_RELATIVE_OPERATORS.has(operator);
  }

  return false;
};

export const buildDefaultConditionLiteral = (fieldType, operator) => {
  if (fieldType === 'boolean') return true;
  if (isEnumCompatibleFieldType(fieldType)) return '';
  if (fieldType === 'checklist') {
    if (VALUELESS_CONDITION_OPERATORS.has(operator)) return null;
    if (CHECKLIST_ITEM_CONDITION_OPERATORS.has(operator)) return '';
    return null;
  }
  if (fieldType === 'tags' || fieldType === 'people') return '';
  if (fieldType === 'number' || fieldType === 'money' || fieldType === 'percentage') {
    if (operator === 'between') return { min: '', max: '' };
    return '';
  }

  if (fieldType === 'measurement') {
    if (VALUELESS_CONDITION_OPERATORS.has(operator)) {
      return null;
    }
    return '';
  }

  if (fieldType === 'date' || fieldType === 'datetime') {
    if (operator === 'within_past' || operator === 'within_next' || operator === 'older_than') {
      return { value: 1, unit: 'days' };
    }
    if (operator === 'is_this') {
      return { value: null, unit: 'week' };
    }
    if (VALUELESS_CONDITION_OPERATORS.has(operator)) {
      return null;
    }
    return '';
  }

  if (fieldType === 'daterange' || fieldType === 'datetimerange') {
    if (operator === 'overlaps_past' || operator === 'overlaps_next') {
      return { value: 1, unit: 'days' };
    }
    if (operator === 'overlaps_this') {
      return { value: null, unit: 'week' };
    }
    if (VALUELESS_CONDITION_OPERATORS.has(operator)) {
      return null;
    }
    return '';
  }

  return '';
};

export const applyConditionOperatorChange = ({ fieldType, operator, currentCondition }) => {
  const isNumericFieldType = fieldType === 'number' || fieldType === 'money' || fieldType === 'percentage';

  if (isNumericFieldType && operator === 'between') {
    const currentLiteral = currentCondition?.value?.literal;
    const currentMin = currentLiteral && typeof currentLiteral === 'object'
      ? currentLiteral.min
      : currentLiteral;
    const currentMax = currentLiteral && typeof currentLiteral === 'object'
      ? currentLiteral.max
      : '';
    return {
      ...currentCondition,
      operator,
      value: {
        type: 'literal',
        literal: {
          min: currentMin ?? '',
          max: currentMax ?? '',
        },
      },
    };
  }

  if (isNumericFieldType && currentCondition?.operator === 'between') {
    const currentLiteral = currentCondition?.value?.literal;
    const fallbackValue = currentLiteral && typeof currentLiteral === 'object'
      ? (currentLiteral.min ?? '')
      : (currentLiteral ?? '');

    if (VALUELESS_CONDITION_OPERATORS.has(operator)) {
      return {
        ...currentCondition,
        operator,
        value: {
          type: 'literal',
          literal: null,
        },
      };
    }

    return {
      ...currentCondition,
      operator,
      value: {
        type: 'literal',
        literal: fallbackValue,
      },
    };
  }

  if (!isDateLikeFieldType(fieldType)) {
    if (VALUELESS_CONDITION_OPERATORS.has(operator)) {
      return {
        ...currentCondition,
        operator,
        value: {
          type: 'literal',
          literal: null,
        },
      };
    }

    if (fieldType === 'checklist' && CHECKLIST_ITEM_CONDITION_OPERATORS.has(operator)) {
      const currentLiteral = currentCondition?.value?.literal;
      return {
        ...currentCondition,
        operator,
        value: {
          type: 'literal',
          literal: typeof currentLiteral === 'string' ? currentLiteral : '',
        },
      };
    }

    return {
      ...currentCondition,
      operator,
    };
  }

  const currentLiteral = currentCondition?.value?.literal;

  if (fieldType === 'date' || fieldType === 'datetime') {
    if (operator === 'within_past' || operator === 'within_next' || operator === 'older_than') {
      const currentValue = currentLiteral && typeof currentLiteral === 'object'
        ? currentLiteral.value
        : null;
      const currentUnit = currentLiteral && typeof currentLiteral === 'object'
        ? currentLiteral.unit
        : null;

      return {
        ...currentCondition,
        operator,
        value: {
          type: 'literal',
          literal: {
            value: currentValue == null || currentValue === '' ? 1 : currentValue,
            unit: normalizeRelativeUnit(currentUnit) || 'days',
          },
        },
      };
    }

    if (operator === 'is_this') {
      const currentUnit = currentLiteral && typeof currentLiteral === 'object'
        ? currentLiteral.unit
        : null;
      return {
        ...currentCondition,
        operator,
        value: {
          type: 'literal',
          literal: {
            value: null,
            unit: normalizeCalendarUnit(currentUnit) || 'week',
          },
        },
      };
    }

    if (VALUELESS_CONDITION_OPERATORS.has(operator)) {
      return {
        ...currentCondition,
        operator,
        value: {
          type: 'literal',
          literal: null,
        },
      };
    }

    return {
      ...currentCondition,
      operator,
      value: {
        type: 'literal',
        literal: typeof currentLiteral === 'string' ? currentLiteral : '',
      },
    };
  }

  if (operator === 'overlaps_past' || operator === 'overlaps_next') {
    const currentValue = currentLiteral && typeof currentLiteral === 'object'
      ? currentLiteral.value
      : null;
    const currentUnit = currentLiteral && typeof currentLiteral === 'object'
      ? currentLiteral.unit
      : null;

    return {
      ...currentCondition,
      operator,
      value: {
        type: 'literal',
        literal: {
          value: currentValue == null || currentValue === '' ? 1 : currentValue,
          unit: normalizeRelativeUnit(currentUnit) || 'days',
        },
      },
    };
  }

  if (operator === 'overlaps_this') {
    const currentUnit = currentLiteral && typeof currentLiteral === 'object'
      ? currentLiteral.unit
      : null;
    return {
      ...currentCondition,
      operator,
      value: {
        type: 'literal',
        literal: {
          value: null,
          unit: normalizeCalendarUnit(currentUnit) || 'week',
        },
      },
    };
  }

  if (VALUELESS_CONDITION_OPERATORS.has(operator)) {
    return {
      ...currentCondition,
      operator,
      value: {
        type: 'literal',
        literal: null,
      },
    };
  }

  return {
    ...currentCondition,
    operator,
    value: {
      type: 'literal',
      literal: typeof currentLiteral === 'string' ? currentLiteral : '',
    },
  };
};
